// @flow
import sumBy from 'lodash/sumBy';
import isNil from 'lodash/isNil';
import shortId from 'shortid';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';

import type {
  FormErrors,
  OperationLimitAggregationField,
  RegulationLimit,
  RegulationLimitGroup
} from './../../../../../../lib/types';
import {
  operationLimitAggregationFieldsEnum,
  operationLimitGroupStatusEnum
} from './../../../../../../lib/enum';
import { withEmptyRow } from './../../../../../../lib/helpers';
import { declineHours } from './../../lib';
import { regulationLimitTypeEnum } from '../../../../../../lib/enum';
import {
  regulationLimitApi,
  regulationLimitGroupApi
} from '../../../../../../lib/api';

// Валидация лимитов
export const validateRegulationLimit = (
  regulationLimit: RegulationLimit,
  group: RegulationLimitGroup
): FormErrors<RegulationLimit> => {
  const errors: FormErrors<RegulationLimit> = {};
  const { type, hours, hoursWeekend } = regulationLimit;
  const {
    hours: maxHours,
    hoursWeekend: maxHoursWeekend,
    usedInWeekends
  } = group;
  const getRequiredFields = () => {
    const commonRequiredFields = ['averageSpeed', 'downtimeRatio'];
    switch (type) {
      case regulationLimitTypeEnum.ovb:
        return [
          'ovbName',
          'orgUnitId',
          'hours',
          'distance',
          ...commonRequiredFields
        ];
      case regulationLimitTypeEnum.employee:
        return usedInWeekends
          ? [
              'employeePositionTitle',
              'distance',
              'hours',
              'hoursWeekend',
              'distanceWeekend',
              ...commonRequiredFields
            ]
          : [
              'employeePositionTitle',
              'distance',
              'hours',
              ...commonRequiredFields
            ];
      case regulationLimitTypeEnum.orgUnit:
      case regulationLimitTypeEnum.oneOffRequests:
        return usedInWeekends
          ? [
              'orgUnitId',
              'distance',
              'hours',
              'hoursWeekend',
              'distanceWeekend',
              ...commonRequiredFields
            ]
          : ['orgUnitId', 'distance', 'hours', ...commonRequiredFields];
      default:
        return [];
    }
  };
  getRequiredFields().forEach(key => {
    if (!regulationLimit[key]) {
      if (
        (key === 'averageSpeed' || key === 'downtimeRatio') &&
        regulationLimit[key] === 0
      ) {
        return;
      }
      errors[key] = 'Обязательное поле';
    }
  });
  if (hours > maxHours) {
    errors.hours = `Время должно быть не более ${declineHours(maxHours)}`;
  }
  if (hoursWeekend > maxHoursWeekend) {
    errors.hoursWeekend = `Время должно быть не более ${declineHours(
      maxHours
    )}`;
  }
  return errors;
};

export const aggregateBy = (
  limits: RegulationLimit[],
  key: $Keys<RegulationLimit>
) => {
  return parseFloat(
    (
      sumBy(limits, key) / limits.filter(limit => !isNil(limit[key])).length
    ).toFixed(2)
  );
};

export const aggregateLimits = (
  limits: RegulationLimit[]
): { [key: OperationLimitAggregationField]: number } => {
  return Object.keys(operationLimitAggregationFieldsEnum).reduce(
    (obj, key) => ({
      ...obj,
      [(key: string)]: aggregateBy(limits, key)
    }),
    {}
  );
};

/**
 * Функция конвертации строки к числу или к пустой строке
 * @param value Строка
 */
export const toStringNumberLimitValue = (value: string) =>
  value.replace(/[^0-9.]/g, '') || '';

/**
 * Функция сохранения группы лимитов
 * @param group Группа
 * @param orgUnitId Прикрепляемое подразделение
 * @returns {Promise<void>}
 */
export const saveGroup = async (
  group: RegulationLimitGroup,
  orgUnitId: number
): Promise<RegulationLimitGroup> => {
  group = {
    ...group,
    timeLimitStatus: operationLimitGroupStatusEnum.draft,
    assignmentLimitStatus: operationLimitGroupStatusEnum.draft
  };
  if (group.id) {
    await regulationLimitGroupApi.update(group);
  } else {
    group.orgUnitId = orgUnitId;
    const { id } = await regulationLimitGroupApi.add(group);
    group.id = id;
  }
  group.regulationLimits = await Promise.all(
    group.regulationLimits.map(async limit => {
      if (limit.id) {
        return await regulationLimitApi.update(limit);
      }
      limit.regulationLimitGroupId = group.id;
      // Только для должностных лиц проставляем orgUnitId
      // У остальных он проставляется в регламенте
      if (limit.type === regulationLimitTypeEnum.employee)
        limit.orgUnitId = group.orgUnitId;
      return await regulationLimitApi.add(limit);
    })
  );
  return group;
};

/**
 * Является ли число NaN или null, undefined
 * @param value Число
 * @returns {boolean}
 */
const isNilOrNaN = (value: ?number) => isNil(value) || isNaN(value);

/**
 * Возвращает массив лимитов с пустым лимитом на конце
 */
export const withEmptyLimit = (
  limits: RegulationLimit[],
  group: RegulationLimitGroup
) => {
  const {
    id: regulationLimitGroupId,
    orgUnitId,
    type,
    hoursWeekend,
    hours,
    distance,
    distanceWeekend,
    averageSpeed,
    downtimeRatio
  } = group;
  return withEmptyRow(limits, {
    emptyRow: {
      type,
      regulationLimitGroupId,
      hours: hours,
      distance,
      hoursWeekend: !isNilOrNaN(hoursWeekend) ? hoursWeekend : undefined,
      distanceWeekend: !isNilOrNaN(distanceWeekend)
        ? distanceWeekend
        : undefined,
      orgUnitId:
        type === regulationLimitTypeEnum.employee ? orgUnitId : undefined,
      key: shortId.generate(),
      averageSpeed: !isNilOrNaN(averageSpeed) ? averageSpeed : undefined,
      downtimeRatio: !isNilOrNaN(downtimeRatio) ? downtimeRatio : undefined
    },
    ignoredEmptyRowKeys: ['key']
  });
};

/**
 * Проверяет пустой ли это лимит
 * @param limit Лимит
 * @param group Группа лимитов
 */
export const isEmptyLimit = (
  limit: RegulationLimit,
  group: RegulationLimitGroup
) => {
  const {
    id: regulationLimitGroupId,
    type,
    orgUnitId,
    hoursWeekend,
    hours,
    distance,
    distanceWeekend,
    averageSpeed,
    downtimeRatio
  } = group;
  return isEqual(omit(limit, 'key'), {
    type,
    hours: hours,
    distance,
    hoursWeekend: !isNilOrNaN(hoursWeekend) ? hoursWeekend : undefined,
    distanceWeekend: !isNilOrNaN(distanceWeekend) ? distanceWeekend : undefined,
    averageSpeed: !isNilOrNaN(averageSpeed) ? averageSpeed : undefined,
    downtimeRatio: !isNilOrNaN(downtimeRatio) ? downtimeRatio : undefined,
    orgUnitId:
      type === regulationLimitTypeEnum.employee ? orgUnitId : undefined,
    regulationLimitGroupId
  });
};
